/**
 * Localization utilities for NUMENAXIS
 * Supports personalized greetings based on gender
 * Designed for easy extension to other languages
 */

// Supported locales
export const LOCALES = {
  LT: 'lt', // Lithuanian
  EN: 'en', // English (future)
};

// Current locale (can be changed dynamically in the future)
let currentLocale = LOCALES.LT;

// Gender types
export const GENDER = {
  FEMALE: 'female',
  MALE: 'male',
};

/**
 * Convert Lithuanian male name to vocative case (šauksmininkas/kreipinys)
 * @param {string} name - The person's first name
 * @returns {string} Name in vocative case
 */
function toVocativeMale(name) {
  if (!name || name.length < 2) return name;
  
  const trimmedName = name.trim();
  
  // Names ending in -ius → -iau (Darius → Dariau, Marius → Mariau)
  // Must check before -us!
  if (trimmedName.endsWith('ius')) {
    return trimmedName.slice(0, -3) + 'iau';
  }
  
  // Names ending in -as → -ai (Jonas → Jonai, Petras → Petrai)
  if (trimmedName.endsWith('as')) {
    return trimmedName.slice(0, -2) + 'ai';
  }
  
  // Names ending in -is → -i (Artūris → Artūri)
  if (trimmedName.endsWith('is')) {
    return trimmedName.slice(0, -2) + 'i';
  }
  
  // Names ending in -us → -au (Titus → Titau)
  if (trimmedName.endsWith('us')) {
    return trimmedName.slice(0, -2) + 'au';
  }
  
  // Names ending in -ys → -y (rare, e.g., Algirdys → Algirdy)
  if (trimmedName.endsWith('ys')) {
    return trimmedName.slice(0, -1);
  }
  
  // Default: return name as-is if no known ending
  return trimmedName;
}

// Localized strings
const translations = {
  [LOCALES.LT]: {
    greetings: {
      [GENDER.FEMALE]: (name) => `Sveika, ${name}!`,
      [GENDER.MALE]: (name) => `Sveikas, ${toVocativeMale(name)}!`,
      default: (name) => `Sveiki, ${name}!`,
    },
    congratulations: {
      [GENDER.FEMALE]: (name) => `Sveikiname, ${name}!`,
      [GENDER.MALE]: (name) => `Sveikiname, ${toVocativeMale(name)}!`,
      default: (name) => `Sveikiname, ${name}!`,
    },
    welcomeBack: {
      [GENDER.FEMALE]: () => `Sveika sugrįžusi namo!`,
      [GENDER.MALE]: () => `Sveikas sugrįžęs namo!`,
      default: () => `Sveiki sugrįžę namo!`,
    },
    genderSelection: {
      label: 'Kaip į tave kreiptis?',
      female: 'Moteris',
      male: 'Vyras',
      hint: 'Šis pasirinkimas naudojamas tik pasisveikinimo kalbai.',
    },
  },
  [LOCALES.EN]: {
    greetings: {
      [GENDER.FEMALE]: (name) => `Welcome, ${name}!`,
      [GENDER.MALE]: (name) => `Welcome, ${name}!`,
      default: (name) => `Welcome, ${name}!`,
    },
    congratulations: {
      [GENDER.FEMALE]: (name) => `Congratulations, ${name}!`,
      [GENDER.MALE]: (name) => `Congratulations, ${name}!`,
      default: (name) => `Congratulations, ${name}!`,
    },
    welcomeBack: {
      [GENDER.FEMALE]: () => `Welcome back!`,
      [GENDER.MALE]: () => `Welcome back!`,
      default: () => `Welcome back!`,
    },
    genderSelection: {
      label: 'How should we address you?',
      female: 'Female',
      male: 'Male',
      hint: 'This is only used for personalized greetings.',
    },
  },
};

/**
 * Get the current locale
 */
export function getLocale() {
  return currentLocale;
}

/**
 * Set the current locale
 * @param {string} locale - The locale code (e.g., 'lt', 'en')
 */
export function setLocale(locale) {
  if (translations[locale]) {
    currentLocale = locale;
  } else {
    console.warn(`Locale '${locale}' not supported. Falling back to Lithuanian.`);
    currentLocale = LOCALES.LT;
  }
}

/**
 * Get a personalized greeting based on gender
 * @param {string} name - The person's name
 * @param {string|null} gender - 'female', 'male', or null/undefined
 * @param {string} locale - Optional locale override
 * @returns {string} The personalized greeting
 */
export function getGreeting(name, gender, locale = currentLocale) {
  const localeStrings = translations[locale] || translations[LOCALES.LT];
  const greetings = localeStrings.greetings;
  
  if (gender === GENDER.FEMALE) {
    return greetings[GENDER.FEMALE](name);
  } else if (gender === GENDER.MALE) {
    return greetings[GENDER.MALE](name);
  } else {
    return greetings.default(name);
  }
}

/**
 * Get a personalized congratulation message based on gender
 * @param {string} name - The person's name
 * @param {string|null} gender - 'female', 'male', or null/undefined
 * @param {string} locale - Optional locale override
 * @returns {string} The personalized congratulation
 */
export function getCongratulation(name, gender, locale = currentLocale) {
  const localeStrings = translations[locale] || translations[LOCALES.LT];
  const congrats = localeStrings.congratulations;
  
  if (gender === GENDER.FEMALE) {
    return congrats[GENDER.FEMALE](name);
  } else if (gender === GENDER.MALE) {
    return congrats[GENDER.MALE](name);
  } else {
    return congrats.default(name);
  }
}

/**
 * Get a personalized "welcome back" message based on gender
 * @param {string|null} gender - 'female', 'male', or null/undefined
 * @param {string} locale - Optional locale override
 * @returns {string} The personalized welcome back message
 */
export function getWelcomeBack(gender, locale = currentLocale) {
  const localeStrings = translations[locale] || translations[LOCALES.LT];
  const welcomeBack = localeStrings.welcomeBack;
  
  if (gender === GENDER.FEMALE) {
    return welcomeBack[GENDER.FEMALE]();
  } else if (gender === GENDER.MALE) {
    return welcomeBack[GENDER.MALE]();
  } else {
    return welcomeBack.default();
  }
}

/**
 * Get gender selection UI strings
 * @param {string} locale - Optional locale override
 * @returns {object} Object with label, female, male, hint
 */
export function getGenderSelectionStrings(locale = currentLocale) {
  const localeStrings = translations[locale] || translations[LOCALES.LT];
  return localeStrings.genderSelection;
}

/**
 * Get gender display name
 * @param {string} gender - 'female' or 'male'
 * @param {string} locale - Optional locale override
 * @returns {string} Localized gender name
 */
export function getGenderDisplayName(gender, locale = currentLocale) {
  const strings = getGenderSelectionStrings(locale);
  if (gender === GENDER.FEMALE) return strings.female;
  if (gender === GENDER.MALE) return strings.male;
  return '';
}
